import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  postNdjsonStream,
} from "./client";
import type {
  AssistantCreditSettings,
  AssistantMessage,
  AssistantMessageFeedback,
  AssistantThread,
  StreamEvent,
} from "@sales-ops-amplify/shared";

export type AssistantPresetsResponse = {
  assistant_presets: Array<{
    id: string;
    type: string;
    icon: string;
    text: string;
    name: string;
    author_name: string;
    category: string;
    short_description?: string;
    long_description?: string;
    visibility: boolean;
    showcase?: boolean;
    autorun?: boolean;
  }>;
  order: string[];
  category_order: string[];
  prompt_paths: Record<string, string[]>;
};

export async function listAssistantPresets(): Promise<AssistantPresetsResponse> {
  return apiGet("/api/assistant/presets");
}

export async function listAssistantThreads(): Promise<{
  threads: AssistantThread[];
}> {
  return apiGet("/api/assistant/threads");
}

export async function createAssistantThread(body?: {
  title?: string;
  contextProductIds?: string[];
}): Promise<{ thread: AssistantThread }> {
  return apiPost("/api/assistant/threads", body ?? {});
}

export async function resolveCommandItemThread(body: {
  commandItemId: string;
  generationId?: string;
  contextProductIds?: string[];
}): Promise<{ thread: AssistantThread }> {
  return apiPost("/api/assistant/threads/command-item", body);
}

export async function getAssistantThread(
  threadId: string,
  opts?: { startSeq?: number; limit?: number },
): Promise<{ thread: AssistantThread; messages: AssistantMessage[] }> {
  const q = new URLSearchParams();
  if (opts?.startSeq != null) q.set("startSeq", String(opts.startSeq));
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return apiGet(`/api/assistant/threads/${threadId}${qs ? `?${qs}` : ""}`);
}

export async function updateAssistantThread(
  threadId: string,
  body: {
    title?: string;
    contextProductIds?: string[];
    creditLimit?: number | null;
    status?: AssistantThread["status"];
  },
): Promise<{ thread: AssistantThread }> {
  return apiPatch(`/api/assistant/threads/${threadId}`, body);
}

export async function deleteAssistantThread(
  threadId: string,
): Promise<{ deleted: boolean }> {
  return apiDelete(`/api/assistant/threads/${threadId}`);
}

export async function setAssistantFeedback(
  threadId: string,
  messageId: string,
  feedback: AssistantMessageFeedback,
): Promise<{ message: AssistantMessage }> {
  return apiPatch(
    `/api/assistant/threads/${threadId}/messages/${messageId}/feedback`,
    { feedback },
  );
}

export async function getAssistantCreditSettings(): Promise<{
  settings: AssistantCreditSettings;
}> {
  return apiGet("/api/assistant/credit-settings");
}

export async function updateAssistantCreditSettings(
  settings: AssistantCreditSettings,
): Promise<{ settings: AssistantCreditSettings }> {
  return apiPut("/api/assistant/credit-settings", settings);
}

export async function listContextProducts(): Promise<{
  products: Array<{ id: string; name: string }>;
  hasLegacy: boolean;
}> {
  return apiGet("/api/assistant/context-products");
}

export type AssistantStreamMeta = {
  threadId?: string;
  messageId?: string;
  durationMs?: number;
  verb?: string;
  steps?: string[];
};

export async function sendAssistantMessage(
  body: {
    message: string;
    threadId?: string;
    contextProductIds?: string[];
    confirmCredits?: boolean;
    /** Short chat-list title (task / sales move). */
    taskTitle?: string;
    /** Model-only context; not stored as the user message. */
    taskContext?: string;
  },
  onEvent: (event: StreamEvent, meta: AssistantStreamMeta) => void,
  signal?: AbortSignal,
): Promise<AssistantStreamMeta> {
  const meta: AssistantStreamMeta = {};
  const path = body.threadId
    ? `/api/assistant/threads/${body.threadId}/messages`
    : "/api/assistant";

  await postNdjsonStream(
    path,
    {
      message: body.message,
      contextProductIds: body.contextProductIds,
      confirmCredits: body.confirmCredits === true,
      ...(body.taskTitle ? { taskTitle: body.taskTitle } : {}),
      ...(body.taskContext ? { taskContext: body.taskContext } : {}),
      ...(body.threadId ? {} : { threadId: body.threadId }),
    },
    (raw) => {
      const event = raw as StreamEvent & Record<string, unknown>;
      if (event.type === "status" && event.label === "thread" && event.threadId) {
        meta.threadId = String(event.threadId);
      }
      if (
        event.type === "status" &&
        event.label === "message_saved"
      ) {
        if (event.messageId) meta.messageId = String(event.messageId);
        if (typeof event.durationMs === "number") meta.durationMs = event.durationMs;
        if (typeof event.verb === "string") meta.verb = event.verb;
        if (Array.isArray(event.steps)) meta.steps = event.steps as string[];
      }
      onEvent(event as StreamEvent, meta);
    },
    signal,
  );

  return meta;
}
